import { FC, useState } from 'react'
import { Box, Button, Chip, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { AnelProgresso } from '@/components/dashboard/AnelProgresso'
import { TituloSecao } from '@/components/dashboard/TituloSecao'
import { formatBRL } from '@/components/dashboard/canais'
import { DashboardResumo, MetaPeriodo, OrigemMeta, PeriodicidadeMeta, TipoMeta } from '@/types/dashboard'
import { tokens } from '@/theme'

/** Ordem das abas: Mensal, Anual e — só se o backend mandar — a campanha de período inteiro. */
const ORDEM_ABAS: PeriodicidadeMeta[] = ['MENSAL', 'ANUAL', 'PERIODO']

const ROTULO_ABA: Record<PeriodicidadeMeta, string> = {
  MENSAL: 'Mensal',
  ANUAL: 'Anual',
  PERIODO: 'Campanha',
}

const ROTULO_ORIGEM: Record<OrigemMeta, string> = {
  INDIVIDUAL: 'Meta individual',
  PADRAO_CAMPANHA: 'Meta padrão da campanha',
  CAMPANHA: 'Meta da campanha',
  COLETIVA: 'Meta coletiva da campanha',
}

/** Tipos na ordem do backend: VOLUME primeiro; RECEITA só aparece se vier em `metas`. */
const ORDEM_TIPOS: TipoMeta[] = ['VOLUME', 'RECEITA']

const ROTULO_TIPO: Record<TipoMeta, string> = {
  VOLUME: 'Volume',
  RECEITA: 'Receita',
}

/** Descrição por tipo (Q25: as métricas não se misturam). */
const DESCRICAO_TIPO: Record<TipoMeta, string> = {
  VOLUME: 'Volume ganho sobre a meta do período.',
  RECEITA: 'Receita Originei confirmada pelo Financeiro, por competência, sobre a meta do período.',
}

/** Rótulo do realizado: "Fechado" para volume ganho, "Realizado" para receita confirmada. */
const ROTULO_REALIZADO: Record<TipoMeta, string> = {
  VOLUME: 'Fechado',
  RECEITA: 'Realizado',
}

/** Só formatação: o número já vem com uma casa do backend ("125,0%"). */
const formatPercentualBackend = (p: number) => `${p.toFixed(1).replace('.', ',')}%`

export interface CardMetasProps {
  /** `resumo.metas` (VC-02). Ausente → cai no `meta` legado. */
  metas?: MetaPeriodo[] | null
  /** `resumo.meta` legado — usado só quando `metas` não veio (backend anterior). */
  metaLegada?: DashboardResumo['meta'] | null
  carregando?: boolean
  modo: 'consultor' | 'admin'
  /** Admin com canal selecionado: a meta é global, não recortada por canal. */
  recortadoPorCanal?: boolean
  /** Admin: "+n campanhas vigentes — ver Campanhas →". */
  onVerCampanhas?: () => void
}

/**
 * Card de metas dos dashboards (VC-02, AN-5 §4). Nenhum número é calculado aqui:
 * `percentual`, `faltam` e `atingida` vêm prontos do `/v1/dashboard/resumo` (contrato §2).
 * O anel recebe o `percentual` do backend; o texto ao lado mostra o valor sem teto.
 *
 * AN-7: as metas vêm agrupadas por `tipo`. Com entradas de RECEITA, aparece o seletor
 * "Volume | Receita"; cada tipo tem a sua descrição e o seu realizado — nada é somado entre
 * eles.
 *
 * Fallback de rollout: sem `metas` (backend anterior ao DEV-3), mantém o comportamento
 * de hoje com o `meta` legado.
 */
export const CardMetas: FC<CardMetasProps> = ({
  metas,
  metaLegada,
  carregando = false,
  modo,
  recortadoPorCanal = false,
  onVerCampanhas,
}) => {
  const tipos = ORDEM_TIPOS.filter((t) => (metas ?? []).some((m) => (m.tipo ?? 'VOLUME') === t))
  const [tipoEscolhido, setTipoEscolhido] = useState<TipoMeta>('VOLUME')
  const tipo: TipoMeta = tipos.includes(tipoEscolhido) ? tipoEscolhido : (tipos[0] ?? 'VOLUME')
  const abas = ORDEM_ABAS.map((p) =>
    (metas ?? []).find((m) => m.periodicidade === p && (m.tipo ?? 'VOLUME') === tipo),
  ).filter((m): m is MetaPeriodo => !!m)
  const [aba, setAba] = useState<PeriodicidadeMeta>('MENSAL')
  const descricao = DESCRICAO_TIPO[tipo]

  const seletor = tipos.includes('RECEITA') ? (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={tipo}
      onChange={(_, v: TipoMeta | null) => v && setTipoEscolhido(v)}
      aria-label="Tipo de meta"
      sx={{ mb: 1.5 }}
    >
      {tipos.map((t) => (
        <ToggleButton key={t} value={t} sx={{ px: 2, py: 0.25 }}>
          {ROTULO_TIPO[t]}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  ) : null

  if (carregando) {
    return (
      <>
        <TituloSecao titulo="Metas" descricao="Volume ganho sobre a meta do período." />
        <EstadoDeLista estado="carregando" minHeight={160} />
      </>
    )
  }

  if (!metas) return <MetaLegada meta={metaLegada ?? null} modo={modo} />

  if (abas.length === 0) {
    return (
      <>
        <TituloSecao titulo="Metas" descricao={descricao} />
        {seletor}
        <EstadoDeLista estado="vazio" mensagem="Sem meta definida" minHeight={160} />
      </>
    )
  }

  const selecionada = abas.find((m) => m.periodicidade === aba) ?? abas[0]

  return (
    <>
      <TituloSecao titulo="Metas" descricao={descricao} />
      {seletor}
      <Tabs
        value={selecionada.periodicidade}
        onChange={(_, v: PeriodicidadeMeta) => setAba(v)}
        aria-label="Período da meta"
        sx={{ mb: 2, minHeight: 36 }}
      >
        {abas.map((m) => (
          <Tab
            key={m.periodicidade}
            value={m.periodicidade}
            label={ROTULO_ABA[m.periodicidade]}
            sx={{ minHeight: 36 }}
          />
        ))}
      </Tabs>
      <PainelMeta meta={selecionada} modo={modo} />
      {modo === 'admin' && selecionada.outrasCampanhasVigentes > 0 && (
        <Button size="small" sx={{ mt: 1.5 }} onClick={onVerCampanhas}>
          {`+${selecionada.outrasCampanhasVigentes} ${
            selecionada.outrasCampanhasVigentes === 1 ? 'campanha vigente' : 'campanhas vigentes'
          } — ver Campanhas →`}
        </Button>
      )}
      {modo === 'admin' && recortadoPorCanal && (
        <Typography
          variant="caption"
          component="div"
          sx={{ color: tokens.textSecondary, mt: 1 }}
        >
          Meta não recortada por canal
        </Typography>
      )}
    </>
  )
}

const PainelMeta: FC<{ meta: MetaPeriodo; modo: 'consultor' | 'admin' }> = ({ meta, modo }) => {
  const campanha = meta.campanha
  const realizado = ROTULO_REALIZADO[meta.tipo ?? 'VOLUME']
  const cabecalho = (
    <Box sx={{ mb: 1.5 }}>
      {campanha && (
        <Typography sx={{ fontWeight: 600, color: tokens.brandInk }}>{campanha.nome}</Typography>
      )}
      <Typography variant="caption" component="div" sx={{ color: tokens.textSecondary }}>
        {meta.rotuloPeriodo}
        {meta.origemMeta ? ` · ${ROTULO_ORIGEM[meta.origemMeta]}` : ''}
      </Typography>
    </Box>
  )
  const premiacao = campanha?.premiacao ? (
    <Typography variant="body2" sx={{ mt: 1.5, color: tokens.brandInk }}>
      Ao atingir: {campanha.premiacao}
    </Typography>
  ) : null

  if (meta.valorMeta == null) {
    const fechado = `${realizado} em ${meta.rotuloPeriodo}: ${formatBRL(Number(meta.fechado))}`
    if (campanha && modo === 'consultor') {
      return (
        <Box>
          {cabecalho}
          <EstadoDeLista
            estado="vazio"
            mensagem="Meta individual não definida"
            detalhe={fechado}
            minHeight={120}
          />
          {premiacao}
        </Box>
      )
    }
    return (
      <Box>
        {campanha && cabecalho}
        <EstadoDeLista estado="vazio" mensagem="Sem meta definida" detalhe={fechado} minHeight={120} />
      </Box>
    )
  }

  return (
    <Box>
      {cabecalho}
      <AnelProgresso
        percentual={meta.percentual ?? 0}
        rotulo={`Meta ${ROTULO_ABA[meta.periodicidade].toLowerCase()}`}
        linhas={[
          { rotulo: realizado, valor: formatBRL(Number(meta.fechado)), destaque: true },
          { rotulo: 'Meta', valor: formatBRL(Number(meta.valorMeta)) },
          {
            rotulo: 'Faltam',
            valor: meta.faltam != null ? formatBRL(Number(meta.faltam)) : '—',
          },
        ]}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
        {meta.percentual != null && (
          <Typography variant="caption" data-testid="meta-percentual" sx={{ color: tokens.textSecondary }}>
            {`Atingimento: ${formatPercentualBackend(Number(meta.percentual))}`}
          </Typography>
        )}
        {meta.atingida && <Chip size="small" color="success" label="Meta atingida" />}
      </Box>
      {premiacao}
    </Box>
  )
}

/**
 * Comportamento de antes do VC-02, preservado só para backend sem `metas` (rollout).
 * Some quando o DEV-3 estiver em todos os ambientes.
 */
const MetaLegada: FC<{ meta: DashboardResumo['meta'] | null; modo: 'consultor' | 'admin' }> = ({
  meta,
  modo,
}) => {
  const temMeta = !!meta && Number(meta.trimestre) > 0
  const progresso = temMeta ? (Number(meta.fechado) / Number(meta.trimestre)) * 100 : 0
  return (
    <>
      <TituloSecao titulo={meta?.nome ?? 'Meta do trimestre'} descricao="Volume fechado sobre a meta." />
      {temMeta ? (
        <AnelProgresso
          percentual={progresso}
          rotulo="Meta do trimestre"
          linhas={[
            { rotulo: 'Fechado', valor: formatBRL(Number(meta.fechado)), destaque: true },
            { rotulo: 'Meta', valor: formatBRL(Number(meta.trimestre)) },
            {
              rotulo: 'Faltam',
              valor: formatBRL(Math.max(0, Number(meta.trimestre) - Number(meta.fechado))),
            },
          ]}
        />
      ) : (
        <EstadoDeLista
          estado="vazio"
          mensagem="Sem meta definida"
          detalhe={
            modo === 'consultor'
              ? 'Quando a Originei definir a meta do trimestre, o progresso aparece aqui.'
              : 'Quando a meta do trimestre for definida, o progresso aparece aqui.'
          }
          minHeight={160}
        />
      )}
    </>
  )
}

export default CardMetas
